import type { Prettify } from '@web-platform/shared-universal-types-utils/src/utils';

type KeyMap = Record<
  string,
  {
    sourceKey: string;
    required?: boolean;
  }
>;

type RemappedObject<TObj extends Record<string, unknown>, TKeyMap extends KeyMap> = Prettify<{
  [
    TargetKey in keyof TKeyMap as TKeyMap[TargetKey]['sourceKey'] extends keyof TObj
      ? TObj[TKeyMap[TargetKey]['sourceKey']] extends undefined
        ? never
        : TargetKey
      : never
  ]: TKeyMap[TargetKey]['sourceKey'] extends keyof TObj
    ? TKeyMap[TargetKey]['required'] extends true
      ? Exclude<TObj[TKeyMap[TargetKey]['sourceKey']], undefined>
      : TObj[TKeyMap[TargetKey]['sourceKey']] | undefined
    : never;
}>;

export const remapObject = <TObj extends Record<string, unknown>, TKeyMap extends KeyMap>(
  keyMap: TKeyMap,
  obj?: TObj,
): RemappedObject<TObj, TKeyMap> => {
  if (!obj) {
    throw new Error(`Object to be remapped was undefined`);
  }

  return Object.keys(keyMap).reduce(
    (result, targetKey) => {
      const { required, sourceKey } = keyMap[targetKey];

      if (Object.prototype.hasOwnProperty.call(obj, sourceKey) && obj[sourceKey] !== undefined) {
        return { ...result, [targetKey]: obj[sourceKey] };
      }

      if (required) {
        throw new Error(
          `sourceKey = "${sourceKey}" is undefined but required in object re-mapping`,
        );
      }

      return result;
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as RemappedObject<TObj, TKeyMap>,
  );
};
